import React from 'react';
import isNil from 'lodash/isNil';
import ScorecardChooser from './ScorecardChooser';
import ScorecardQuestionnaire from './ScorecardQuestionnaire';
import ScorecardResults from './ScorecardResults';
import FileUploadCallCardActions from 'actions/FileUploadCallCardActions';
import ScorecardQuestionnaireActions from 'actions/scorecards/ScorecardQuestionnaireActions';
import './Scorecard.styl';
import {NA} from 'records/Scorecard';

export default class Scorecard extends React.PureComponent {
  static defaultProps = {
    goBack: () => FileUploadCallCardActions.toggleScorecard(false),
  };

  constructor(props) {
    super(props);
    this.state = {
      userExplicitlyClickedEdit: false,
    };
    this.goToChooser = this.goToChooser.bind(this);
    this.goToEditScreen = this.goToEditScreen.bind(this);
    this.onChooseScorecard = this.onChooseScorecard.bind(this);
  }

  componentDidMount() {
    if (this.props.scorecards) {
      this.setStateFromProps();
    }
  }

  componentWillUnmount() {
    FileUploadCallCardActions.toggleScorecard(false);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.scorecards && nextProps.scorecards !== this.props.scorecards) {
      this.setStateFromProps(nextProps);
    }
  }

  setStateFromProps() {
    const scorecard = this.getActiveScorecard();
    // TODO improve this
    if (scorecard && this.shouldShowResultsFor(scorecard)) {
      FileUploadCallCardActions.toggleScorecardResults.defer(true);
    }
  }

  getActiveScorecard() {
    const {scorecards, scorecardId} = this.props;
    const {selectedScorecardIndex} = this.state;
    if (!scorecards) {
      return null;
    }
    if (scorecards.size === 1) {
      return scorecards.get(0);
    } else if (scorecardId) {
      return scorecards.find(scorecard => scorecard.id === scorecardId);
    } else if (!isNil(selectedScorecardIndex)) {
      return scorecards.get(selectedScorecardIndex);
    }
    return null;
  }

  shouldShowResultsFor(scorecard) {
    // if they are not in the middle of scoring the call and
    // they have already reviewed it, show the results
    const answers = this.props.answers && this.props.answers.get(scorecard.id);
    return !answers && scorecard.hasBeenReviewedBy(this.props.sessionUser.id);
  }

  onChooseScorecard(scorecard) {
    this.setState({
      selectedScorecardIndex: this.props.scorecards.indexOf(scorecard),
    });
    if (this.shouldShowResultsFor(scorecard)) {
      FileUploadCallCardActions.toggleScorecardResults(true);
    }
  }

  goToEditScreen(result) {
    const {modelId} = this.props;
    const scorecard = this.getActiveScorecard();
    FileUploadCallCardActions.toggleScorecardResults(false);
    this.setState({
      userExplicitlyClickedEdit: true,
    });

    const answers = this.props.answers && this.props.answers.get(scorecard.id);
    if (!answers) {
      result.answers.forEach(answer => {
        ScorecardQuestionnaireActions.addAnswer(
          modelId,
          scorecard.id,
          answer.questionId,
          answer.value !== null ? String(answer.value) : NA
        );
        if (answer.comment) {
          ScorecardQuestionnaireActions.addComment(
            modelId,
            scorecard.id,
            answer.questionId,
            answer.comment
          );
        }
        if (answer.reason) {
          ScorecardQuestionnaireActions.addReason(
            modelId,
            scorecard.id,
            answer.questionId,
            answer.reason
          );
        }
      });
    }
  }

  goToChooser() {
    FileUploadCallCardActions.toggleScorecardResults(false);
    this.setState({
      userExplicitlyClickedEdit: false,
    });
    this.setState({selectedScorecardIndex: null});
  }

  goToResults() {
    FileUploadCallCardActions.toggleScorecardResults(true);
    this.setState({
      userExplicitlyClickedEdit: false,
    });
  }

  componentToRender() {
    const scorecard = this.getActiveScorecard();
    const {
      answers,
      reasons,
      comments,
      scorecardId,
      taggedUsers,
      topNav,
      goBack,
      onSubmit,
      extraReview,
      areScorecardResultsToggled,
      readonly,
    } = this.props;

    // userExplicitlyClickedEdit - the user specifically asked to edit.
    // this needs combined with the readonly prop for that scenario (livecoaching)
    // in order to show the edit grid when the user specifically asks for it
    // (we dont otherwise ever show the edit grid in this flow)
    const {userExplicitlyClickedEdit} = this.state;

    const showResults = (readonly && !userExplicitlyClickedEdit) || areScorecardResultsToggled;

    // dependent components need to know if there are more than one scorecard in the current profile in order to route
    // back buttons correctly
    const hasMultipleScorecards =
      !scorecardId && this.props.scorecards && this.props.scorecards.size > 1;

    if (!scorecard) {
      return (
        <ScorecardChooser
          newCallCard={this.props.newCallCard}
          scorecards={this.props.scorecards}
          onChooseScorecard={this.onChooseScorecard}
          goBack={goBack}
          topNav={topNav}
          suppressUnscored={readonly}>
          {extraReview && extraReview(true)}
        </ScorecardChooser>
      );
    } else if (showResults) {
      return (
        <ScorecardResults
          newCallCard={this.props.newCallCard}
          scorecard={scorecard}
          title={this.props.title}
          sessionUser={this.props.sessionUser}
          goToChooser={this.goToChooser}
          hasMultipleScorecards={hasMultipleScorecards}
          goToEditScreen={this.goToEditScreen}
          schemeLabels={this.props.schemeLabels}
          goBack={goBack}
          topNav={topNav}>
          {!hasMultipleScorecards && extraReview && extraReview(false)}
        </ScorecardResults>
      );
    } else {
      return (
        <ScorecardQuestionnaire
          newCallCard={this.props.newCallCard}
          key={this.props.scorecardId}
          model={this.props.model}
          onSubmit={() => {
            onSubmit && onSubmit();
            this.goToResults();
          }}
          scorecard={scorecard}
          modelId={this.props.modelId}
          title={this.props.title}
          sessionUser={this.props.sessionUser}
          isOwnCall={this.props.isOwnCall}
          hasMultipleScorecards={hasMultipleScorecards}
          goToChooser={this.goToChooser}
          goToResults={this.goToResults}
          goBack={goBack}
          answers={answers && answers.get(scorecard.id)}
          reasons={reasons && reasons.get(scorecard.id)}
          comments={comments && comments.get(scorecard.id)}
          taggedUsers={taggedUsers && taggedUsers.get(scorecard.id)}
          reasonSchemes={this.props.reasonSchemes}
          schemeLabels={this.props.schemeLabels}
          isSaving={this.props.isSaving}
          topNav={topNav}>
          {!hasMultipleScorecards && extraReview && extraReview(true)}
        </ScorecardQuestionnaire>
      );
    }
  }

  render() {
    const {navHeight} = this.props;
    const component = this.componentToRender();
    const style = navHeight ? {height: `calc(100% - ${navHeight}px)`} : {};
    return (
      <div style={style} className="scorecard">
        {component}
      </div>
    );
  }
}
